import React, { ChangeEvent, useEffect, useState } from 'react';
import {
  Box,
  Divider,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Heading,
  Input,
  Select,
  Switch,
  Text,
  useColorModeValue,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { WattwatchersMeter } from 'clipsal-cortex-types/src/api/api-ww-meter';

import { patch } from '../../../api/api-helpers';
import { ElectricalConfiguration, MeteringConfiguration, SiteDataToSave } from '../../../api/api-site';
import { useAppDispatch } from '../../../app/hooks';
import { SelectAddress } from '../../../common/components/address/SelectAddress';
import { CustomButton } from '../../../common/components/CustomButton';
import { CustomDatePicker } from '../../../common/components/date-picker/CustomDatePicker';
import { VOLTAGE_CONFIGURATIONS } from '../../../common/constants';
import { useSiteRouteChangeContext } from '../../../context/context-exports';
import { formatDate } from '../../../utils/datetime-utils';
import { setHasUnsavedChanges } from '../../wizard/wizardSlice';
import { WizardSubRouteCardWrapper } from '../../wizard/WizardSubRouteCardWrapper';
import { checkMeterPhasing } from '../meter-setup/meter-phasing-helpers';
import { addMeters, selectRawMeters } from '../meter-setup/meterSetupSlice';
import { usePatchSiteMutation, usePostSiteMutation, useSite } from '../siteApi';
import { setSiteId } from '../siteSlice';
import { ControlledLoadTutorialModal } from './ControlledLoadTutorialModal';
import { SiteDetailsFormData, siteDetailsFormSchema } from './site-details-form-helpers';
import { SiteImage } from './SiteImage';
import { VoltageConfigurationTutorialModal } from './VoltageConfigurationTutorialModal';

export function SiteDetails() {
  const { onMoveForward } = useSiteRouteChangeContext();
  const methods = useForm<SiteDetailsFormData>({ resolver: yupResolver(siteDetailsFormSchema) });
  const {
    register,
    control,
    reset,
    setError,
    watch,
    handleSubmit: handleFormSubmit,
    formState: { errors },
  } = methods;
  const [siteImageLink, setSiteImageLink] = useState<string | null>(null);
  const dispatch = useAppDispatch();
  const { id } = useParams<{ id: string }>();
  const isNewSite = id === 'new';
  const { site, isLoading: isLoadingSite } = useSite();
  const [postSite, { isLoading: isPostingSite }] = usePostSiteMutation();
  const [updateSite, { isLoading: isUpdatingSite }] = usePatchSiteMutation();
  const isSubmitting = isPostingSite || isUpdatingSite;
  const rawMeters = useSelector(selectRawMeters);
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    isOpen: isControlledLoadModalOpen,
    onOpen: onControlledLoadModalOpen,
    onClose: onControlledLoadModalClose,
  } = useDisclosure();
  const selectedPhaseValue = watch('voltageConfiguration') as ElectricalConfiguration;
  const dividerColor = useColorModeValue('#0000001A', 'dusk100.400');
  const voltageConfigRef = register(`voltageConfiguration`);

  useEffect(() => {
    // Annoying hack because chakra-ui automatically sets input `autocomplete` attribute to "new-password", even if
    // manually set to "off". Might be worth submitting a PR since this is fairly annoying.
    if (control._fields?.address?._f?.ref) {
      const ref = control._fields.address?._f?.ref as HTMLInputElement;
      ref.autocomplete = 'off';
    }
  }, [control._fields]);

  useEffect(() => {
    // when the site is loaded, reset the form values
    if (id !== 'new' && site && !isLoadingSite) {
      dispatch(setHasUnsavedChanges(false));
      reset({
        installationDate: site.install_date?.split(' ')[0],
        name: site.site_name,
        address: site.site_address_line1,
        state: site.site_state,
        postCode: site.site_zipcode,
        country: site.site_country,
        timezone: site.timezone,
        latitude: Number(site.site_latitude),
        longitude: Number(site.site_longitude),
        voltageConfiguration: site.electrical_config,
      });
    }
  }, [id, dispatch, isLoadingSite, site, reset]);

  async function handleSubmit(data: SiteDetailsFormData) {
    // Validate auto-populated site details, e.g. postcode/timezone/state etc. These are required to get Cortex working
    // correctly for the user.
    if (!data.timezone || !data.postCode || !data.country || !data.state) {
      toast({
        title: 'Invalid site address',
        description:
          'Please make sure the site address is either added from the auto complete options, or set manually.',
        status: 'error',
        isClosable: true,
      });
      setError('address', {
        message: 'Please either select from the options below, or click "Enter address manually".',
      });
      return;
    }

    const siteData: SiteDataToSave = {
      install_date: formatDate(new Date(data.installationDate)) + ' 00:00:00',
      site_name: data.name,
      site_city: data.city,
      site_state: data.state,
      site_zipcode: data.postCode,
      site_country: data.country,
      site_address_line1: data.address,
      timezone: data.timezone,
      street_address: data.address.split(',')?.[0],
      metering_configuration: 'NET' as MeteringConfiguration,
      electrical_config: data.voltageConfiguration as ElectricalConfiguration,
      site_latitude: null,
      site_longitude: null,
    };

    if (data.latitude && data.longitude) {
      siteData.site_latitude = data.latitude.toString();
      siteData.site_longitude = data.longitude.toString();
    }

    try {
      if (isNewSite) {
        const newSite = await postSite(siteData).unwrap();
        dispatch(setHasUnsavedChanges(false));
        dispatch(setSiteId(newSite.clipsal_solar_id));
        onMoveForward(newSite.clipsal_solar_id);
      } else {
        const updatedSite = await updateSite({
          ...siteData,
          clipsal_solar_id: Number(id),
          is_consumption_only: site.is_consumption_only,
        }).unwrap();
        dispatch(setHasUnsavedChanges(false));
        // update phasing in meters if required as it needs to match site phasing
        const meterDevices = Object.values(rawMeters);
        if (meterDevices.length) {
          const { isCorrectlyPhased, updatedMeters } = checkMeterPhasing(
            data.voltageConfiguration as ElectricalConfiguration,
            meterDevices
          );
          if (!isCorrectlyPhased) {
            // Update each meter respectively
            const meters = await Promise.all(
              updatedMeters.map<Promise<WattwatchersMeter>>((m) =>
                patch(`/fleet/wattwatchers/installed_meters/${m.installed_device_id}`, m)
              )
            );
            toast({
              title: `Meter voltage references updated!`,
              description: 'Circuit voltage references has been updated as per site electrical configuration.',
              status: 'info',
              isClosable: true,
            });
            dispatch(addMeters(meters));
          }
        }
        onMoveForward(updatedSite.clipsal_solar_id);
      }
    } catch (e) {
      console.error(e);

      toast({
        title: 'Unable to save this site.',
        description: 'Please try again. If this persists, contact an administrator.',
        status: 'error',
        isClosable: true,
      });
    }
  }

  async function handleChangeVoltageConfig(e: ChangeEvent<HTMLSelectElement>) {
    const value = e.currentTarget.value;
    await voltageConfigRef.onChange(e);

    // Check local storage to see if the voltage config type popup has been disabled
    // If not, display it.
    if (localStorage.getItem(`voltageConfigModalDisabled_${value}`) !== 'true') onOpen();
  }

  const handleUpdateSiteImageLink = (imageLink: string) => {
    setSiteImageLink(imageLink);
  };

  return (
    <FormProvider {...methods}>
      <WizardSubRouteCardWrapper p={[0]}>
        <Box>
          <Heading my={3} ml={[4, 6]} size={'lg'}>
            Site Details
          </Heading>
          <Divider borderColor={dividerColor} opacity={1} />
          <Box
            data-testid="site-details-form"
            onChange={() => dispatch(setHasUnsavedChanges(true))}
            onSubmit={handleFormSubmit(handleSubmit)}
            as={'form'}
            px={[4, 6]}
          >
            <FormControl my={3} isInvalid={!!errors.installationDate} id="name">
              <FormLabel>Installation date</FormLabel>
              <Controller
                control={control}
                name="installationDate"
                render={({ field: { onChange, value } }) => (
                  <CustomDatePicker
                    maxDate={new Date()}
                    inputProps={{
                      'data-testid': `system-installation-date`,
                      placeholder: 'dd/mm/yyyy',
                    }}
                    popperPlacement="top-start"
                    dateFormat="dd/MM/yyyy"
                    onChange={(date) => date && onChange(formatDate(date))}
                    selected={value ? new Date(value) : null}
                  />
                )}
              />
              <FormErrorMessage>Installation date is required.</FormErrorMessage>
            </FormControl>

            <FormControl my={3} isInvalid={!!errors.name} id="name">
              <FormLabel>Site name</FormLabel>
              <Input
                data-private
                data-testid="site-name"
                placeholder={'John Smith Site'}
                {...register('name')}
                type="text"
              />
              <FormErrorMessage>Site name is required.</FormErrorMessage>
            </FormControl>

            <SelectAddress<SiteDetailsFormData>
              testId="site-address"
              inputTitle="Site address"
              addLatitudeLongitude
              addTimezone
              onUpdateSiteImageLink={handleUpdateSiteImageLink}
            />

            <FormControl isInvalid={!!errors.voltageConfiguration} mt={3} id={'voltageConfiguration'}>
              <FormLabel>Voltage type</FormLabel>
              <Select
                data-testid="site-voltage-configuration"
                name={voltageConfigRef.name}
                onBlur={voltageConfigRef.onBlur}
                ref={voltageConfigRef.ref}
                onChange={handleChangeVoltageConfig}
              >
                <option value={0}>Select a voltage type...</option>
                {VOLTAGE_CONFIGURATIONS.map(({ label, value }, i) => (
                  <option key={`voltage-config-option-${i}`} value={value}>
                    {label}
                  </option>
                ))}
              </Select>
              <FormErrorMessage>Voltage configuration is required.</FormErrorMessage>
            </FormControl>

            <FormControl mt={3}>
              <FormLabel>Controlled load</FormLabel>
              <Flex align="center">
                <Text mr={2}>Is there a controlled load on site?</Text>
                <Switch
                  size="md"
                  data-testid="site-has-controlled-load"
                  onChange={(e) => {
                    const dontShowControlledLoadModal = localStorage.getItem('dontShowControlledLoadModal');
                    dontShowControlledLoadModal !== 'true' && e.target.checked && onControlledLoadModalOpen();
                  }}
                  colorScheme={'primaryBranding'}
                  id="site-has-controlled-load"
                />
              </Flex>
            </FormControl>

            {isControlledLoadModalOpen && (
              <ControlledLoadTutorialModal isOpen={isControlledLoadModalOpen} onClose={onControlledLoadModalClose} />
            )}

            <SiteImage siteImageUrl={siteImageLink} />

            <Divider my={5} color={dividerColor} />

            <CustomButton isLoading={isSubmitting} isDisabled={isLoadingSite} data-testid="site-details-form-submit">
              Next: System Details
            </CustomButton>
          </Box>
        </Box>
      </WizardSubRouteCardWrapper>

      {isOpen && (
        <VoltageConfigurationTutorialModal isOpen={isOpen} onClose={onClose} selectedPhaseValue={selectedPhaseValue} />
      )}
    </FormProvider>
  );
}
